export const PLACEHOLDER_VALUE = '\u2014'

/**
 * A multilingual label object returned by the backend API.
 * Contains translations keyed by locale code.
 */
export interface ILabelObject {
  key: string
  en: string
  nl: string
}

/**
 * Extracts the localized string from a backend LabelObject.
 * Falls back to English if the locale is not `en` or `nl`, then to the `key` field.
 */
export function localizeLabel(label: ILabelObject | string | null | undefined, locale: string = 'en'): string {
  if (!label) return ''
  if (typeof label === 'string') return label
  if (locale === 'nl') return label.nl || label.en || label.key
  return label.en || label.nl || label.key
}

/**
 * Formats a number as a Euro currency string using nl-NL locale.
 * Default 2 fraction digits; pass 0 for whole-number display.
 */
export function formatCurrency(amount: number, fractionDigits: number = 2): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount)
}

const CENTS_PER_EURO = 100

/**
 * Integer cents → the euro string shown in a currency input.
 */
export function centsToEuroString(cents: number): string {
  return String(cents / CENTS_PER_EURO)
}

/**
 * Integer cents → a nl-NL euro string ('66,50') for prefilling a currency input,
 * so what the field shows matches what the user is asked to type.
 */
export function centsToEuroInput(cents: number): string {
  return (cents / CENTS_PER_EURO).toFixed(2).replace('.', ',')
}

/**
 * Euro string from a currency input → integer cents; empty/invalid → null.
 *
 * The last `,` or `.` in the string is the decimal separator and anything before
 * it is grouping, so both '1.234,56' (NL) and '1,234.56' (EN) yield 123456. A
 * lone '.' stays a decimal point ('7.50' → 750), which leaves '1.234' ambiguous —
 * it reads as €1,23. Unavoidable without knowing the user's locale, and the
 * inputs prefill via centsToEuroInput, so a bare grouped integer is rare.
 */
export function euroStringToCents(euro: string): number | null {
  // '66,-' is Dutch shorthand for a whole amount. The separator is kept, so
  // '1.234,-' still reads its comma as the decimal point and yields 1234.
  const cleaned = euro.replace(/[\s€]/g, '').replace(/([,.])-+$/, '$100')
  if (cleaned === '') return null

  const decimalAt = Math.max(cleaned.lastIndexOf(','), cleaned.lastIndexOf('.'))
  const whole = decimalAt === -1 ? cleaned : cleaned.slice(0, decimalAt)
  const fraction = decimalAt === -1 ? '' : cleaned.slice(decimalAt + 1)

  const normalized = `${whole.replace(/[.,]/g, '') || '0'}.${fraction || '0'}`
  if (!/^-?\d+\.\d+$/.test(normalized)) return null

  return Math.round(Number(normalized) * CENTS_PER_EURO)
}

/**
 * Extracts initials from a name string (max 2 characters, uppercased).
 * Returns empty string for empty/blank input.
 */
export function getInitials(name: string): string {
  if (!name?.trim()) return ''

  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('')
}

/**
 * Formats a card number string into groups of 4 digits (e.g. "3528 1070 6112 8401").
 * Strips any existing whitespace/dashes, then inserts a space every 4 characters.
 */
export function formatCardNumber(cardNumber: string): string {
  const digits = cardNumber.replace(/[\s-]/g, '')
  return digits.replace(/(.{4})(?=.)/g, '$1 ')
}

/** Last four digits only, for traveller-facing card numbers. */
export function maskCardNumber(cardNumber: string): string {
  const digits = cardNumber.replace(/[\s-]/g, '')
  if (digits.length === 0) return ''

  return formatCardNumber(digits.slice(-4))
}

const MASK_DOT = '\u2022'

/**
 * One mask dot per hidden digit, last four visible. Length follows the number:
 * a 10-digit number hides six digits. `maskCardNumber` stays last-four only.
 */
export function maskCardNumberFull(cardNumber: string): string {
  const digits = cardNumber.replace(/\D/g, '')
  if (digits.length === 0) return ''
  if (digits.length <= 4) return digits

  return `${MASK_DOT.repeat(digits.length - 4)}${digits.slice(-4)}`
}

/**
 * Calculates the number of days from today until a target date.
 * Returns null if the date is null/undefined or invalid.
 * Negative values mean the date is in the past.
 */
export function getDaysUntil(isoDate: string | null | undefined): number | null {
  if (!isoDate) return null

  try {
    const target = new Date(isoDate)
    const today = new Date()
    // Compare date-only (strip time component)
    target.setHours(0, 0, 0, 0)
    today.setHours(0, 0, 0, 0)
    const diffMs = target.getTime() - today.getTime()
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  } catch {
    return null
  }
}

/**
 * Formats an ISO 8601 date string into a numeric `dd-mm-yyyy` date (nl-NL).
 * Returns an em-dash for null/undefined/invalid values.
 */
export function formatDateNumeric(isoDate: string | null | undefined): string {
  if (!isoDate) return PLACEHOLDER_VALUE

  const date = new Date(isoDate)
  if (Number.isNaN(date.getTime())) return PLACEHOLDER_VALUE

  return date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/**
 * Formats an ISO 8601 date string into a localized short date.
 * Returns an em-dash for null/undefined values.
 */
export function formatDate(isoDate: string | null | undefined, locale: string = 'en'): string {
  if (!isoDate) return PLACEHOLDER_VALUE

  try {
    const date = new Date(isoDate)
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return PLACEHOLDER_VALUE
  }
}

/**
 * Formats an ISO 8601 datetime as `HH:mm:ss` in Europe/Amsterdam.
 * Returns an em-dash for null/undefined/invalid values.
 */
export function formatTime(isoDate: string | null | undefined): string {
  if (!isoDate) return PLACEHOLDER_VALUE

  const date = new Date(isoDate)
  if (Number.isNaN(date.getTime())) return PLACEHOLDER_VALUE

  return date.toLocaleTimeString('nl-NL', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Europe/Amsterdam',
  })
}

/**
 * Formats an ISO 8601 datetime as `dd-mm-yyyy HH:mm` (nl-NL, 24h).
 * Returns an em-dash for null/undefined/invalid values.
 */
export function formatDateTime(isoDate: string | null | undefined): string {
  if (!isoDate) return PLACEHOLDER_VALUE

  const date = new Date(isoDate)
  if (Number.isNaN(date.getTime())) return PLACEHOLDER_VALUE

  const datePart = date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const timePart = date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', hour12: false })

  return `${datePart} ${timePart}`
}
